import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { AppShell } from '@/components/AppShell';
import { NoteCard } from '@/components/note';
import { UserAvatar, pubkeyToNpub } from '@/components/user';
import { useAuthor } from '@/hooks/useAuthor';
import { getDisplayName } from '@/lib/getDisplayName';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';
import { Search as SearchIcon } from 'lucide-react';
import { dedupeEvents } from '@/lib/jumble';
import { useFeed } from '@/contexts/FeedContext';

export function SearchPage() {
  useSeoMeta({ title: 'Search — Jumble', description: 'Search Nostr notes and people.' });
  const { nostr } = useNostr();
  const { feedRelays } = useFeed();
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState('');

  const notes = useQuery({
    queryKey: ['jumble', 'search-notes', submitted, [...feedRelays].sort()],
    enabled: submitted.length > 1,
    queryFn: async ({ signal }) => {
      const group = feedRelays.length > 0 ? nostr.group(feedRelays) : nostr;
      try {
        const events = (await group.query([{ kinds: [1], search: submitted, limit: 30 }], { signal: signal ?? AbortSignal.timeout(5000) })) as NostrEvent[];
        return dedupeEvents(events);
      } catch {
        return [] as NostrEvent[];
      }
    },
  });

  const people = useQuery({
    queryKey: ['jumble', 'search-people', submitted],
    enabled: submitted.length > 1,
    queryFn: async ({ signal }) => {
      try {
        const events = (await nostr.query([{ kinds: [0], search: submitted, limit: 20 }], { signal: signal ?? AbortSignal.timeout(5000) })) as NostrEvent[];
        return dedupeEvents(events);
      } catch {
        return [] as NostrEvent[];
      }
    },
  });

  const go = () => setSubmitted(text.trim());

  return (
    <AppShell title="Search">
      <div className="border-b p-4">
        <div className="flex gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Search notes and people…" onKeyDown={(e) => e.key === 'Enter' && go()} autoFocus />
          <Button onClick={go}><SearchIcon className="h-4 w-4" /> Search</Button>
        </div>
      </div>
      {!submitted && (
        <p className="p-8 text-center text-sm text-muted-foreground">Type to search across Nostr. NIP-50 search depends on relay support.</p>
      )}
      {submitted && (
        <div className="pb-20 md:pb-4">
          <h3 className="border-b px-4 py-2 text-sm font-bold">People</h3>
          {people.isLoading ? <div className="p-4"><Skeleton className="h-12 w-full" /></div> :
            (people.data ?? []).length === 0 ? <p className="px-4 py-3 text-sm text-muted-foreground">No people found.</p> :
            (people.data ?? []).map((e) => <PersonRow key={e.id} pubkey={e.pubkey} />)}
          <h3 className="border-b px-4 py-2 text-sm font-bold">Notes</h3>
          {notes.isLoading ? <div className="p-4"><Skeleton className="h-20 w-full" /></div> :
            (notes.data ?? []).length === 0 ? <p className="px-4 py-3 text-sm text-muted-foreground">No notes found.</p> :
            (notes.data ?? []).map((e) => <NoteCard key={e.id} event={e} />)}
        </div>
      )}
    </AppShell>
  );
}

export function PersonRow({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const name = getDisplayName(metadata, pubkey);
  return (
    <Link to={`/${pubkeyToNpub(pubkey)}`} className="flex items-center gap-3 border-b px-4 py-3 transition-colors hover:bg-accent/40">
      <UserAvatar pubkey={pubkey} />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold">{name}</span>
        {metadata?.about && <span className="block truncate text-sm text-muted-foreground">{metadata.about}</span>}
      </span>
    </Link>
  );
}
